import { motion } from 'framer-motion';
import { 
  Target,
  GraduationCap, 
  Briefcase, 
  MapPin, 
  Clock, 
  Heart,
  CheckCircle
} from 'lucide-react';
import React from 'react';

import type { OnboardingStepProps, OnboardingData } from './onboardingTypes';
import { 
  CAREER_GOALS_OPTIONS, 
  HOW_HEARD_OPTIONS, 
  WORK_PREFERENCE_OPTIONS, 
  URGENCY_OPTIONS,
  MOTIVATION_OPTIONS
} from './onboardingTypes';

const OnboardingStep3_GoalsAndPreferences: React.FC<OnboardingStepProps> = ({
  data,
  onDataChange,
  questionType
}) => {
  const handleGoalToggle = (goal: string) => {
    const currentGoals = data.careerGoals || [];
    const updatedGoals = currentGoals.includes(goal)
      ? currentGoals.filter(g => g !== goal)
      : [...currentGoals, goal];
    
    onDataChange({ careerGoals: updatedGoals });
  };

  const handleHowHeardChange = (source: string) => {
    onDataChange({ howHeardAboutUs: source });
  };

  // Career Goals
  if (questionType === 'goals') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Target className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold vscode-text-primary mb-2">What are your career goals?</h2>
          <p className="vscode-text-tertiary">Select all that apply to help us personalize your experience</p>
        </div>

        <div className="grid grid-cols-1 gap-2 max-h-80 overflow-y-auto pr-2">
          {CAREER_GOALS_OPTIONS.map((goal) => (
            <motion.button
              key={goal}
              onClick={() => handleGoalToggle(goal)}
              className={`p-3 rounded-lg border text-left transition-all duration-200 text-sm ${
                (data.careerGoals || []).includes(goal)
                  ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
                  : 'vscode-border vscode-bg-tertiary/50 vscode-text-secondary hover:border-cyan-400/30'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center space-x-3">
                {(data.careerGoals || []).includes(goal) && (
                  <CheckCircle className="w-5 h-5 text-cyan-400" />
                )}
                <span className="font-medium">{goal}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  // Discovery - How did you hear about us
  if (questionType === 'discovery') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold vscode-text-primary mb-2">How did you hear about us?</h2>
          <p className="vscode-text-secondary">Help us understand how you discovered GoGrabJob</p>
        </div>

        <div className="grid grid-cols-1 gap-2 max-h-64 overflow-y-auto pr-2">
          {HOW_HEARD_OPTIONS.map((source) => (
            <motion.button
              key={source}
              onClick={() => handleHowHeardChange(source)}
              className={`p-3 rounded-lg border text-left transition-all duration-200 text-sm ${
                data.howHeardAboutUs === source
                  ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-400'
                  : 'vscode-border vscode-bg-tertiary/50 vscode-text-primary hover:border-cyan-400/30'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{source}</span>
                {data.howHeardAboutUs === source && (
                  <CheckCircle className="w-5 h-5 text-cyan-400" />
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    );
  }

  // Completion
  if (questionType === 'completion') {
    return (
      <div className="text-center space-y-6">
        <Clock className="w-12 h-12 vscode-text-tertiary mx-auto mb-4" />
        <h2 className="text-2xl font-bold vscode-text-primary mb-2">Almost Done!</h2>
        <p className="vscode-text-tertiary">Ready to complete your setup</p>
      </div>
    );
  }

  // Fallback
  return (
    <div className="text-center space-y-6">
      <div className="text-center">
        <Clock className="w-12 h-12 vscode-text-tertiary mx-auto mb-4" />
        <h2 className="text-2xl font-bold vscode-text-primary mb-2">Loading...</h2>
        <p className="vscode-text-tertiary">Preparing next step...</p>
      </div>
    </div>
  );
};

export default React.memo(OnboardingStep3_GoalsAndPreferences);
